/**
 * MyDomainsPage - Shows listings from user's preferred domains/categories
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { listingsAPI, settingsAPI } from '@/lib/api';
import ListingCard from '@/components/listings/ListingCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, AlertCircle, Heart, Loader2 } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const MyDomainsPage = () => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    
    // Clear state when coming from floating menu
    const isNewVisit = useRef(true);
    useEffect(() => {
        const navFlag = sessionStorage.getItem('nav_to_my_domains');
        if (navFlag) {
            sessionStorage.removeItem('nav_to_my_domains');
            isNewVisit.current = true;
        }
    }, []);
    
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [hasPreferences, setHasPreferences] = useState(true);
    const loadMoreRef = useRef(null);
    const [itemsPerPage, setItemsPerPage] = useState(48);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Grid settings matching AllListingsPage
    const [gridSettings, setGridSettings] = useState({
        grid_columns_desktop: 4,
        grid_columns_tablet: 3,
        grid_columns_mobile: 2,
    });
    
    useSEO({
        title: 'Categoriile mele | Colectarium',
        description: 'Piese din categoriile tale preferate'
    });
    
    // Detect mobile
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Check if single column mode
    const singleColumn = isMobile && gridSettings.grid_columns_mobile === 1;
    
    // Load settings
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await settingsAPI.get();
                setItemsPerPage(res.data.listings_per_page || 48);
                setGridSettings({
                    grid_columns_desktop: res.data.grid_columns_desktop || 4,
                    grid_columns_tablet: res.data.grid_columns_tablet || 3,
                    grid_columns_mobile: res.data.grid_columns_mobile || 2,
                });
            } catch (error) {
                console.error('Error loading settings:', error);
            }
        };
        loadSettings();
    }, []);
    
    // Redirect if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: '/my-domains' } });
        }
    }, [isAuthenticated, navigate]);
    
    // Fetch listings from user's preferred domains
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchListings = async () => {
            if (currentPage > 1) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            try {
                const skip = (currentPage - 1) * itemsPerPage;
                const res = await listingsAPI.getMyDomains({ 
                    limit: itemsPerPage, 
                    skip,
                    sort_by: 'created_at', 
                    sort_order: 'desc' 
                });
                
                const data = res.data?.listings || res.data || [];
                const total = res.data?.total || data.length;
                
                if (currentPage > 1) {
                    setListings(prev => [...prev, ...data]);
                } else {
                    setListings(data);
                }
                
                setTotalItems(total);
                setHasMore(skip + data.length < total);
                setHasPreferences(total > 0 || currentPage === 1);
            } catch (error) {
                console.error('Error fetching my domains listings:', error);
                if (error.response?.status === 401) {
                    navigate('/login');
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };
        
        fetchListings();
    }, [currentPage, isAuthenticated, navigate, itemsPerPage]);
    
    // Infinite scroll observer
    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore]);
    
    if (!isAuthenticated) {
        return null;
    }
    
    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Heart className="w-8 h-8 text-primary" />
                        Categoriile mele
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {totalItems} piese din categoriile tale preferate
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    onClick={() => navigate('/dashboard?tab=settings')}
                    className="flex items-center gap-2"
                >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Setări domenii</span>
                </Button>
            </div>
            
            {/* Content */}
            {loading && listings.length === 0 ? (
                <div 
                    className="grid gap-6 category-grid"
                    style={{
                        '--grid-cols-mobile': gridSettings.grid_columns_mobile,
                        '--grid-cols-tablet': gridSettings.grid_columns_tablet,
                        '--grid-cols-desktop': gridSettings.grid_columns_desktop,
                    }}
                >
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-[340px] rounded-2xl" />
                    ))}
                </div>
            ) : listings.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-2xl">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Nicio piesă găsită</h2>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        Nu există piese în domeniile tale selectate sau nu ai selectat încă niciun domeniu de interes.
                    </p>
                    <Button onClick={() => navigate('/dashboard?tab=settings')}>
                        <Settings className="w-4 h-4 mr-2" />
                        Configurează domeniile
                    </Button>
                </div>
            ) : (
                <>
                    <div 
                        className="grid gap-6 category-grid"
                        style={{
                            '--grid-cols-mobile': gridSettings.grid_columns_mobile,
                            '--grid-cols-tablet': gridSettings.grid_columns_tablet,
                            '--grid-cols-desktop': gridSettings.grid_columns_desktop,
                        }}
                    >
                        {listings.map((listing) => (
                            <ListingCard 
                                key={listing.listing_id} 
                                listing={listing}
                                singleColumn={singleColumn}
                            />
                        ))}
                    </div>
                    
                    {/* Infinite scroll loader - matching AllListingsPage */}
                    <div 
                        ref={loadMoreRef} 
                        className="flex justify-center py-8"
                        data-testid="infinite-scroll-trigger"
                    >
                        {loadingMore && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Se încarcă...</span>
                            </div>
                        )}
                        {!hasMore && listings.length > 0 && (
                            <p className="text-muted-foreground text-sm">
                                Ai văzut toate cele {totalItems} piese din domeniile tale
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default MyDomainsPage;
